import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  BatteryProposalEquipmentPreviewDraft,
  BatteryProposalInputsDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/** Matches the gateway-side PowurOS Proposal Input cache TTL */
const PREVIEW_STALE_TIME_MS = 30_000

/**
 * Side-effect-free battery equipment preview for a drafted Battery System Type.
 * Keyed on the full draft so a stale in-flight response can never land in the
 * active key; switching drafts switches keys and aborts the abandoned request.
 */
export default function useBatteryProposalEquipmentPreview(
  proposalId: string | undefined,
  draft: BatteryProposalEquipmentPreviewDraft,
  options?: { enabled?: boolean },
): UseQueryResult<BatteryProposalInputsDto> {
  return useQuery({
    queryKey: queryKeys.batteryProposal.equipmentPreview(
      proposalId ?? ``,
      draft,
    ),
    queryFn: ({ signal }) => {
      if (!proposalId) {
        throw new Error(`Battery-Only Proposal id is required`)
      }

      return batteryProposalService.previewEquipment(
        proposalId,
        {
          batterySystemType: draft.batterySystemType,
          ...(draft.financeType
            ? {
                financePlan: {
                  financeType: draft.financeType,
                  fundingGroup: draft.fundingGroup,
                  ...(draft.loanProgramId != null
                    ? { loanProgramId: draft.loanProgramId }
                    : {}),
                },
              }
            : {}),
        },
        signal,
      )
    },
    enabled:
      Boolean(proposalId) &&
      Boolean(draft.financeType) &&
      (options?.enabled ?? true),
    staleTime: PREVIEW_STALE_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: false,
  })
}
